<template>
  <div
    :class="[
      'flex items-center gap-1 rounded px-2 py-1 border text-sm whitespace-nowrap',
      badgeClasses
    ]"
  >
    <v-icon
      v-if="!isExpired && (isUrgent || showDate)"
      :icon="mdiAlert"
      size="16"
      :color="iconColor"
    />
    <span>{{ label }}</span>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { differenceInHours, differenceInDays, parseISO, format } from 'date-fns'
import { mdiAlert } from '@mdi/js'

const props = withDefaults(
  defineProps<{
    expiresAt: string
    showDate?: boolean
  }>(),
  { showDate: false }
)

const now = computed(() => new Date())

const hoursRemaining = computed(() => differenceInHours(parseISO(props.expiresAt), now.value))
const daysRemaining = computed(() => differenceInDays(parseISO(props.expiresAt), now.value))

const isExpired = computed(() => parseISO(props.expiresAt).getTime() < now.value.getTime())

const isUrgent = computed(() => !isExpired.value && hoursRemaining.value < 24)

const variant = computed<'expired' | 'danger' | 'warning' | 'success'>(() => {
  if (isExpired.value) return 'expired'
  if (hoursRemaining.value < 24) return 'danger'
  if (daysRemaining.value <= 3) return 'warning'
  return 'success'
})

const badgeClasses = computed(() => {
  const map = {
    expired: 'bg-[#f0f0f4] border-[#c6c6d0] text-[#52526f]',
    danger: 'bg-[#fce9ea] border-[#da1e28] text-[#16161d]',
    warning: 'bg-[#fef9e7] border-[#f1c21b] text-[#16161d]',
    success: 'bg-[#eafbef] border-[#198038] text-[#16161d]'
  }
  return map[variant.value]
})

const iconColor = computed(() => {
  return variant.value === 'danger' ? '#da1e28' : undefined
})

const label = computed(() => {
  if (isExpired.value) {
    if (props.showDate) {
      try {
        return `Expirado em ${format(parseISO(props.expiresAt), "dd/MM/yyyy 'às' HH:mm")}`
      } catch {
        return 'Expirado'
      }
    }
    return 'Expirado'
  }
  if (props.showDate) {
    try {
      return `Final do convite: ${format(parseISO(props.expiresAt), "dd/MM/yyyy 'às' HH:mm")}`
    } catch {
      return props.expiresAt
    }
  }
  if (hoursRemaining.value < 24) {
    const hours = Math.max(0, hoursRemaining.value)
    return `${hours} horas restantes`
  }
  const days = Math.max(0, daysRemaining.value)
  return `${days} dias restantes`
})
</script>
